// Prevent multiple initializations when script is loaded more than once
if (!window.buildABundleInitialized) {
  window.buildABundleInitialized = true;

  // Use event delegation for click events - works for both page sections and modals
  document.addEventListener('click', function(e) {
    // Handle toggle button clicks
    const toggleButton = e.target.closest('.build-a-bundle__categories-toggle');
    if (toggleButton) {
      const container = toggleButton.closest('.build-a-bundle');
      const bottomSection = container?.querySelector('.build-a-bundle__bottom');
      
      if (bottomSection) {
        bottomSection.classList.toggle('build-a-bundle__bottom--open');
        // Update all toggle buttons in this container to stay in sync
        const allToggles = container.querySelectorAll('.build-a-bundle__categories-toggle');
        allToggles.forEach(toggle => {
          toggle.classList.toggle('build-a-bundle__categories-toggle--open', bottomSection.classList.contains('build-a-bundle__bottom--open'));
        });
      }
      return;
    }

    // Handle tier threshold link clicks
    const tierLink = e.target.closest('a[href="#eligible-collections"]');
    if (tierLink && tierLink.closest('.build-a-bundle__tier-threshold')) {
      e.preventDefault();
      
      const container = tierLink.closest('.build-a-bundle');
      const bottomSection = container?.querySelector('.build-a-bundle__bottom');
      
      if (bottomSection) {
        // Ensure bottom section is open
        if (!bottomSection.classList.contains('build-a-bundle__bottom--open')) {
          bottomSection.classList.add('build-a-bundle__bottom--open');
          
          // Also update all toggle buttons state
          const allToggles = container.querySelectorAll('.build-a-bundle__categories-toggle');
          allToggles.forEach(toggle => {
            toggle.classList.add('build-a-bundle__categories-toggle--open');
          });
        }
        
        // Smooth scroll to the eligible collections section
        bottomSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  });
}

// Cart-related functionality needs to wait for DOM
// Also guarded to prevent duplicate initialization
if (!window.buildABundleCartInitialized) {
  window.buildABundleCartInitialized = true;
  
  document.addEventListener('DOMContentLoaded', function() {
    // Fetch collection data
  const fetchCollectionData = async (collection) => {
    let productIdsInCollection = [];

    const collectionId = collection.getAttribute('data-collection-id');
    const ids = await fetchCollectionById(collectionId);
    productIdsInCollection = productIdsInCollection.concat(ids);

    return productIdsInCollection;
  };

  // Function to update tiers based on cart data
  const updateTiersFromCart = async (cart) => {
    // Reset all active states first
    const eligibleCollections = document.querySelectorAll('.build-a-bundle__eligible-collection');
    eligibleCollections.forEach(collection => {
      collection.classList.remove('build-a-bundle__eligible-collection--active');
    });

    // Build collection id -> product ids map (each collection's products)
    const collectionProductIds = new Map();
    for (const collection of eligibleCollections) {
      const collectionId = collection.getAttribute('data-collection-id');
      const productIdsInCollection = await fetchCollectionData(collection);
      collectionProductIds.set(collectionId, {
        element: collection,
        productIds: productIdsInCollection,
        multiplier: parseInt(collection.getAttribute('data-multiplier'), 10) || 1
      });
    }

    // Assign each cart item to the first collection (in DOM order) it belongs to.
    // - One product in multiple collections counts toward only one collection (avoids over-counting tiers).
    // - Multiple products from the same collection all add to that collection's quantity (tier count stays 1).
    const collectionQuantities = new Map();
    for (const item of cart.items) {
      const productGid = `gid://shopify/Product/${item.product_id}`;
      for (const [collectionId, data] of collectionProductIds) {
        if (data.productIds.includes(productGid)) {
          const current = collectionQuantities.get(collectionId) || 0;
          collectionQuantities.set(collectionId, current + item.quantity);
          break;
        }
      }
    }

    // Mark collections active only if they meet their multiplier (and have assigned quantity)
    for (const [collectionId, data] of collectionProductIds) {
      const totalQuantity = collectionQuantities.get(collectionId) || 0;
      if (totalQuantity >= data.multiplier) {
        // Query the element to ensure we have a fresh reference in case the DOM has been updated
        const element = document.querySelector(`.build-a-bundle__eligible-collection[data-collection-id="${collectionId}"]`);
        if (element) {
          element.classList.add('build-a-bundle__eligible-collection--active');
        }
      }
    }

    updateTiers();
  };

  // Initial cart load
  fetch('/cart.js')
  .then(response => response.json())
  .then(updateTiersFromCart);

  // Subscribe to cart update events
  const cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.cartData) {
      updateTiersFromCart(event.cartData);
    }
  });

  // Clean up subscription when page unloads
  window.addEventListener('beforeunload', () => {
    if (cartUpdateUnsubscriber) {
      cartUpdateUnsubscriber();
    }
  });

  // Fetch a specific collection by ID using Shopify Storefront API
  const fetchCollectionById = async (collectionId) => {
    let allProductIds = [];

    const query = `
      {
        collection(id: "gid://shopify/Collection/${collectionId}") {
          products(first: 250) {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch("https://gyget1-q6.myshopify.com/api/2025-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": "b099aff945ea6c642ef1c611dd8a4a2e" // public storefront token
        },
        body: JSON.stringify({ query })
      });

      const data = await response.json();

      if (data.data?.collection?.products) {
        const products = data.data.collection.products;
        allProductIds = allProductIds.concat(products.edges.map(edge => edge.node.id));
      }
    } catch (error) {
      console.error('Error fetching collection:', error);
    }

    return allProductIds;
  };

  function updateTiers() {
    // Reset all tier active states first
    const tiers = document.querySelectorAll('.build-a-bundle__tier');
    tiers.forEach(tier => {
      tier.classList.remove('build-a-bundle__tier--active');
    });

    // Count distinct collections that are active (each cart item counts toward at most one collection)
    const activeCollectionElements = document.querySelectorAll('.build-a-bundle__eligible-collection--active');
    const activeCollectionIds = new Set();
    activeCollectionElements.forEach(el => {
      const id = el.getAttribute('data-collection-id');
      if (id) activeCollectionIds.add(id);
    });
    const activeCollectionsCount = activeCollectionIds.size;

    tiers.forEach(tier => {
      const thresholdEl = tier.querySelector('.build-a-bundle__tier-threshold strong');
      if (!thresholdEl) return;
      const threshold = parseInt(thresholdEl.textContent, 10);
      if (activeCollectionsCount >= threshold) {
        tier.classList.add('build-a-bundle__tier--active');
      }
    });
  }
  });
}
